import type { PaymentApi } from "../../api/PaymentApi";
import type { BalanceTransactionFetchOptions } from "../../generated/dto/BalanceTransactionFetchOptions";
import type { BalanceTransactionStaff } from "../../generated/dto/BalanceTransactionStaff";
import type { CashTransactionStaff } from "../../generated/dto/CashTransactionStaff";
import type { CsvExportOptions } from "../../generated/dto/CsvExportOptions";
import type { ManualCaptureResult } from "../../generated/dto/ManualCaptureResult";
import type { OrderFetchOptions } from "../../generated/dto/OrderFetchOptions";
import type { OrderPaymentFetchOptions } from "../../generated/dto/OrderPaymentFetchOptions";
import type { OrderPaymentRef } from "../../generated/dto/OrderPaymentRef";
import type { OrderPaymentStaff } from "../../generated/dto/OrderPaymentStaff";
import type { OrderPaymentStaffSearchParams } from "../../generated/dto/OrderPaymentStaffSearchParams";
import type { OrderPaymentStaffSearchResult } from "../../generated/dto/OrderPaymentStaffSearchResult";
import type { OrderRef } from "../../generated/dto/OrderRef";
import type { OrderSettlementTransferStaff } from "../../generated/dto/OrderSettlementTransferStaff";
import type { OrderStaff } from "../../generated/dto/OrderStaff";
import type { PaymentAttempt } from "../../generated/dto/PaymentAttempt";
import type { PaymentCustomerSession } from "../../generated/dto/PaymentCustomerSession";
import type { PaymentRefundFetchOptions } from "../../generated/dto/PaymentRefundFetchOptions";
import type { PaymentRefundStaff } from "../../generated/dto/PaymentRefundStaff";
import type { PaymentSendPaymentToReaderRequest } from "../../generated/dto/PaymentSendPaymentToReaderRequest";
import type { PaymentSourceStaff } from "../../generated/dto/PaymentSourceStaff";
import type { PaymentStartPaymentRequest } from "../../generated/dto/PaymentStartPaymentRequest";
import type { SettlementNormalizationPreview } from "../../generated/dto/SettlementNormalizationPreview";
import type { SettlementNormalizationResult } from "../../generated/dto/SettlementNormalizationResult";
import type { StripeTransactionStaff } from "../../generated/dto/StripeTransactionStaff";
import type { TransactionOtherStaff } from "../../generated/dto/TransactionOtherStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PaymentFrontendService {
  constructor(private readonly api: PaymentApi) {}
  async getOrderPaymentStaff(id: number, options?: OrderPaymentFetchOptions): Promise<OrderPaymentStaff> {
    return this.api.getOrderPaymentStaff(id, options)
  }
  async getPaymentSourceStaff(id: number): Promise<PaymentSourceStaff> {
    return this.api.getPaymentSourceStaff(id)
  }
  async getOrderSettlementTransferStaff(id: number): Promise<OrderSettlementTransferStaff> {
    return this.api.getOrderSettlementTransferStaff(id)
  }
  async getStripeTransactionStaff(id: number): Promise<StripeTransactionStaff> {
    return this.api.getStripeTransactionStaff(id)
  }
  async getCashTransactionStaff(id: number): Promise<CashTransactionStaff> {
    return this.api.getCashTransactionStaff(id)
  }
  async getTransactionOtherStaff(id: number): Promise<TransactionOtherStaff> {
    return this.api.getTransactionOtherStaff(id)
  }
  async searchOrderPaymentStaff(params: OrderPaymentStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<OrderPaymentStaffSearchResult>> {
    return this.api.searchOrderPaymentStaff(params, page, perPage)
  }
  async exportOrderPaymentStaffSearchToCsv(params: OrderPaymentStaffSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportOrderPaymentStaffSearchToCsv(params, options)
  }
  async searchOrderPaymentStaffFull(params: OrderPaymentStaffSearchParams, page: number, perPage: number, options?: OrderPaymentFetchOptions): Promise<PaginationResult<OrderPaymentStaff>> {
    return this.api.searchOrderPaymentStaffFull(params, page, perPage, options)
  }
  async getOrderPaymentStaffOrderId(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    return this.api.getOrderPaymentStaffOrderId(id, options)
  }
  async getOrderPaymentStaffStripeTransaction(id: number): Promise<StripeTransactionStaff | null> {
    return this.api.getOrderPaymentStaffStripeTransaction(id)
  }
  async getOrderPaymentStaffRefunds(id: number, options?: PaymentRefundFetchOptions): Promise<Array<PaymentRefundStaff>> {
    return this.api.getOrderPaymentStaffRefunds(id, options)
  }
  async getOrderPaymentStaffCashTransaction(id: number): Promise<CashTransactionStaff | null> {
    return this.api.getOrderPaymentStaffCashTransaction(id)
  }
  async getOrderPaymentStaffBalanceTransaction(id: number, options?: BalanceTransactionFetchOptions): Promise<BalanceTransactionStaff | null> {
    return this.api.getOrderPaymentStaffBalanceTransaction(id, options)
  }
  async getOrderPaymentStaffTransactionOther(id: number): Promise<TransactionOtherStaff | null> {
    return this.api.getOrderPaymentStaffTransactionOther(id)
  }
  async cancelPaymentStaff(payment: OrderPaymentRef): Promise<void> {
    return this.api.cancelPaymentStaff(payment)
  }
  async cancelPaymentMember(payment: OrderPaymentRef): Promise<void> {
    return this.api.cancelPaymentMember(payment)
  }
  async completePaymentStaff(order: OrderRef): Promise<void> {
    return this.api.completePaymentStaff(order)
  }
  async completePaymentMember(order: OrderRef): Promise<void> {
    return this.api.completePaymentMember(order)
  }
  async manualCapturePaymentsStaff(includePreviousDays: boolean): Promise<ManualCaptureResult> {
    return this.api.manualCapturePaymentsStaff(includePreviousDays)
  }
  async paymentAttemptStaff(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    return this.api.paymentAttemptStaff(payment)
  }
  async paymentAttemptMember(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    return this.api.paymentAttemptMember(payment)
  }
  async paymentAttemptsStaff(order: OrderRef): Promise<Array<PaymentAttempt>> {
    return this.api.paymentAttemptsStaff(order)
  }
  async paymentAttemptsMember(order: OrderRef): Promise<Array<PaymentAttempt>> {
    return this.api.paymentAttemptsMember(order)
  }
  async paymentCustomerSessionStaff(payment: OrderPaymentRef): Promise<PaymentCustomerSession> {
    return this.api.paymentCustomerSessionStaff(payment)
  }
  async paymentCustomerSessionMember(payment: OrderPaymentRef): Promise<PaymentCustomerSession> {
    return this.api.paymentCustomerSessionMember(payment)
  }
  async preparePaymentStaff(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    return this.api.preparePaymentStaff(payment)
  }
  async preparePaymentMember(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    return this.api.preparePaymentMember(payment)
  }
  async previewMultiDaySettlementStaff(order: OrderRef): Promise<SettlementNormalizationPreview> {
    return this.api.previewMultiDaySettlementStaff(order)
  }
  async repairMultiDaySettlementStaff(order: OrderRef): Promise<SettlementNormalizationResult> {
    return this.api.repairMultiDaySettlementStaff(order)
  }
  async sendPaymentToReaderStaff(request: PaymentSendPaymentToReaderRequest): Promise<void> {
    return this.api.sendPaymentToReaderStaff(request)
  }
  async startPaymentStaff(request: PaymentStartPaymentRequest): Promise<OrderPaymentRef> {
    return this.api.startPaymentStaff(request)
  }
  async startPaymentMember(request: PaymentStartPaymentRequest): Promise<OrderPaymentRef> {
    return this.api.startPaymentMember(request)
  }
}
